import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiFetch } from '../api/client';

interface AlarmReportContextType {
  alarmReportLinks: Map<string, string>;
  linkAlarmToReport: (alarmId: string, reportId: string) => void;
  getReportIdForAlarm: (alarmId: string) => string | undefined;
}

const AlarmReportContext = createContext<AlarmReportContextType | undefined>(undefined);

export function AlarmReportProvider({ children }: { children: ReactNode }) {
  const [alarmReportLinks, setAlarmReportLinks] = useState<Map<string, string>>(new Map());

  // On mount, load existing links from all reports that have a linked_alarm_id
  useEffect(() => {
    apiFetch<{ reports: any[] }>('/reports?limit=500')
      .then(data => {
        const map = new Map<string, string>();
        for (const r of data.reports || []) {
          if (r.linked_alarm_id != null) {
            map.set(String(r.linked_alarm_id), String(r.id));
          }
        }
        setAlarmReportLinks(map);
      })
      .catch(() => {}); // fail silently — links just won't show until refresh
  }, []);

  const linkAlarmToReport = (alarmId: string, reportId: string) => {
    setAlarmReportLinks(prev => new Map(prev).set(alarmId, reportId));
    // Persist to API — fire and forget
    apiFetch(`/reports/${reportId}`, {
      method: 'PATCH',
      body: JSON.stringify({ linked_alarm_id: parseInt(alarmId) }),
    }).catch(() => {});
  };

  const getReportIdForAlarm = (alarmId: string): string | undefined => {
    return alarmReportLinks.get(alarmId);
  };

  return (
    <AlarmReportContext.Provider value={{ alarmReportLinks, linkAlarmToReport, getReportIdForAlarm }}>
      {children}
    </AlarmReportContext.Provider>
  );
}

export function useAlarmReport() {
  const context = useContext(AlarmReportContext);
  if (context === undefined) {
    throw new Error('useAlarmReport must be used within an AlarmReportProvider');
  }
  return context;
}
